import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

const MatchesView = ({ matches = [], isAdmin, onDeleteMatch, token }) => {
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [matchPlayers, setMatchPlayers] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadMatchPlayers = async () => {
      if (!matches || matches.length === 0) return;
      
      setLoading(true);
      try {
        const players = await api.getPlayers(token);
        
        if (!Array.isArray(players)) return;

        const playersByMatch = {};
        
        for (const match of matches) {
          const matchPlayerData = players.filter(p => 
            p.history && 
            Array.isArray(p.history) && 
            p.history.some(h => h.match_id === match.id || h.date === match.match_date)
          ).map(p => {
            const performance = p.history.find(h => h.match_id === match.id || h.date === match.match_date);
            return { ...p, matchPerformance: performance };
          });
          
          playersByMatch[match.id] = matchPlayerData;
        }
        
        setMatchPlayers(playersByMatch);
      } catch (error) {
        console.error('Error loading match players:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMatchPlayers();
  }, [matches, token]);

  const toggleMatch = (matchId) => {
    setExpandedMatch(expandedMatch === matchId ? null : matchId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getMatchStats = (matchId) => {
    const players = matchPlayers[matchId] || [];
    const totalGoals = players.reduce((sum, p) => sum + (p.matchPerformance?.goals || 0), 0);
    const totalSaves = players.reduce((sum, p) => sum + (p.matchPerformance?.saves || 0), 0);
    const totalAssists = players.reduce((sum, p) => sum + (p.matchPerformance?.assists || 0), 0);
    return { totalGoals, totalSaves, totalAssists, playerCount: players.length };
  };

  const handleDelete = async (matchId) => {
    if (window.confirm('Are you sure you want to delete this match? This action cannot be undone.')) {
      await onDeleteMatch(matchId);
    }
  };

  if (!matches || matches.length === 0) {
    return (
      <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-16 text-center shadow-lg animate-fadeIn">
        <div className="w-20 h-20 bg-background rounded-full flex items-center justify-center mx-auto mb-6 border border-outline-variant/30 shadow-inner">
          <span className="material-symbols-outlined text-4xl text-stone-800">history</span>
        </div>
        <h3 className="text-3xl sm:text-4xl font-black text-on-background font-lexend mb-3">No Archives Found</h3>
        <p className="text-stone-600 font-manrope max-w-md mx-auto text-lg">Your match history will appear here once games are logged.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn pb-24">
      {/* Header */}
      <div className="bg-surface-container border border-outline-variant/20 rounded-3xl p-6 sm:p-8 shadow-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-on-background text-2xl font-lexend font-bold mb-1">Match Archives</h2>
          <p className="text-stone-600 text-sm font-manrope">Historical fixture data and player logs</p>
        </div>
        <div className="flex items-center gap-3 bg-background px-4 py-2 rounded-xl border border-outline-variant/30">
          <span className="material-symbols-outlined text-primary">database</span>
          <span className="text-on-background font-lexend font-bold">{matches.length} Records</span>
        </div>
      </div>

      {/* Matches List */}
      <div className="space-y-5">
        {matches.map((match) => {
          const stats = getMatchStats(match.id);
          const players = matchPlayers[match.id] || [];
          
          return (
            <div key={match.id} className="bg-surface-container border border-outline-variant/20 rounded-3xl overflow-hidden shadow-lg transition-all">
              {/* Match Header Button */}
              <button
                onClick={() => toggleMatch(match.id)}
                className="w-full p-6 sm:p-8 hover:bg-surface-container-high transition-colors flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center border border-primary/20 flex-shrink-0 text-primary shadow-lg shadow-primary/10">
                    <span className="material-symbols-outlined text-2xl" style={{fontVariationSettings: "'FILL' 1"}}>calendar_month</span>
                  </div>
                  <div className="text-left">
                    <h3 className="text-on-background font-lexend font-bold text-xl">{formatDate(match.match_date)}</h3>
                    <p className="text-stone-600 font-manrope text-xs mt-1 uppercase tracking-widest font-bold">
                      Fixture #{match.id} • {stats.playerCount} Players
                    </p>
                    {match.team_a_score !== null && match.team_a_score !== undefined && (
                      <div className="flex items-center gap-3 mt-2">
                        <span className={`font-lexend font-black text-lg ${match.team_a_score > match.team_b_score ? 'text-primary' : 'text-stone-600'}`}>
                          {match.team_a_score}
                        </span>
                        <span className="text-stone-800 text-xs font-bold uppercase tracking-widest">vs</span>
                        <span className={`font-lexend font-black text-lg ${match.team_b_score > match.team_a_score ? 'text-blue-700' : 'text-stone-600'}`}>
                          {match.team_b_score}
                        </span>
                        {match.team_a_score === match.team_b_score && (
                          <span className="text-[10px] px-2 py-0.5 rounded-md bg-stone-500/10 text-stone-600 font-bold uppercase tracking-widest">Draw</span>
                        )}
                      </div>
                    )}
                    {match.potm_name && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <span className="material-symbols-outlined text-primary text-[16px]" style={{fontVariationSettings: "'FILL' 1"}}>military_tech</span>
                        <span className="text-primary font-manrope text-xs font-bold uppercase tracking-widest">POTM: {match.potm_name}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center w-full sm:w-auto justify-between sm:justify-end gap-6 sm:gap-8">
                  {/* Quick Stats */}
                  <div className="flex items-center gap-6 text-center">
                    <div>
                      <p className="text-rose-700 font-lexend font-black text-2xl">{stats.totalGoals}</p>
                      <p className="text-stone-700 font-manrope text-[10px] uppercase tracking-widest font-bold">Goals</p>
                    </div>
                    <div>
                      <p className="text-emerald-700 font-lexend font-black text-2xl">{stats.totalAssists}</p>
                      <p className="text-stone-700 font-manrope text-[10px] uppercase tracking-widest font-bold">Assists</p>
                    </div>
                    <div>
                      <p className="text-purple-700 font-lexend font-black text-2xl">{stats.totalSaves}</p>
                      <p className="text-stone-700 font-manrope text-[10px] uppercase tracking-widest font-bold">Saves</p>
                    </div>
                  </div>

                  <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center border border-outline-variant/30 text-stone-600 group-hover:text-primary transition-colors">
                    <span className="material-symbols-outlined">
                      {expandedMatch === match.id ? 'expand_less' : 'expand_more'}
                    </span>
                  </div>
                </div>
              </button>

              {/* Expanded Content */}
              {expandedMatch === match.id && (
                <div className="border-t border-outline-variant/20 p-6 sm:p-8 bg-background/50">
                  <h4 className="text-on-background font-lexend font-bold text-lg mb-6 flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary">analytics</span>
                    Player Performances
                  </h4>
                  
                  {players.length === 0 ? (
                    <div className="text-center py-8 text-stone-700 font-manrope">No player data available for this fixture.</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                      {players.map((player) => {
                        const perf = player.matchPerformance;
                        return (
                          <div key={player.id} className="bg-surface-container rounded-2xl p-4 border border-outline-variant/10 flex items-center gap-4 hover:bg-surface-container-high transition-colors">
                            {/* Avatar */}
                            <div className="relative flex-shrink-0">
                              <div className="w-12 h-12 bg-surface-container-highest rounded-xl flex items-center justify-center border border-outline-variant/30">
                                <span className="text-on-background font-lexend font-bold">{player.name.charAt(0)}</span>
                              </div>
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <p className="text-on-background font-lexend font-bold truncate">{player.name}</p>
                              <p className="text-stone-700 font-manrope text-[10px] uppercase tracking-widest font-bold mt-0.5">{player.position}</p>
                            </div>

                            {/* Stats */}
                            <div className="flex items-center gap-4 text-center">
                              {perf?.goals > 0 && (
                                <div>
                                  <p className="text-rose-700 font-lexend font-black">{perf.goals}</p>
                                  <p className="text-stone-700 text-[9px] font-bold uppercase">G</p>
                                </div>
                              )}
                              {perf?.assists > 0 && (
                                <div>
                                  <p className="text-emerald-700 font-lexend font-black">{perf.assists}</p>
                                  <p className="text-stone-700 text-[9px] font-bold uppercase">A</p>
                                </div>
                              )}
                              {perf?.saves > 0 && (
                                <div>
                                  <p className="text-purple-700 font-lexend font-black">{perf.saves}</p>
                                  <p className="text-stone-700 text-[9px] font-bold uppercase">SV</p>
                                </div>
                              )}
                              
                              {/* Rating */}
                              <div className="pl-4 border-l border-outline-variant/30 text-right">
                                <p className="text-primary font-lexend font-black text-lg">
                                  {Math.round(((perf?.goals || 0) * 3 + (perf?.assists || 0) * 2 + (perf?.saves || 0) * 1) / 2) || '-'}
                                </p>
                                <p className="text-primary/70 text-[9px] font-bold uppercase tracking-widest">RTG</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Delete Button */}
                  {isAdmin && (
                    <div className="mt-8 flex justify-end pt-6 border-t border-outline-variant/10">
                      <button
                        onClick={() => handleDelete(match.id)}
                        className="flex items-center gap-2 px-6 py-3 bg-error/10 text-error rounded-xl font-lexend font-bold text-sm hover:bg-error hover:text-background transition-colors active:scale-95"
                      >
                        <span className="material-symbols-outlined text-[20px]">delete_forever</span>
                        Purge Record
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      )}
    </div>
  );
};

export default MatchesView;
